import { NgModule } from '@angular/core';
import {
  provideRouter,
  RouterModule,
  Routes,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import {
  CouponPermission,
  InvoicePermission,
  RoomPermission,
  VendorPermission,
} from './core/models/entities/permission';
import { AuthenticatedGuard } from './core/guards/authenticated.guard';
import { RoomStepDirtyGuard } from './rooms/guards/room-step-dirty.guard';
import { HomePageComponent } from './public/pages/home-page/home-page.component';
import { MyBookingPage } from './public/pages/booking-detail-page/my-booking-page.component';
import { PermissionsPageComponent } from './public/pages/permissions-page/permissions-page.component';
import { GeneralTermsPageComponent } from './public/pages/general-terms-page/general-terms-page.component';
import { PrivacyPolicyPageComponent } from './public/pages/privacy-policy-page/privacy-policy-page.component';
import { PricingPageComponent } from './public/pages/pricing-page/pricing-page.component';
import { NotFoundPageComponent } from './core/pages/not-found-page/not-found-page.component';
import { UnlockDoorPageComponent } from './public/pages/unlock-door/unlock-door-page.component';
import { AboutPageComponent } from './public/pages/about-page.component';
import { PartnersResellersPageComponent } from './public/pages/partners-resellers-page/partners-resellers-page.component';
import { VendorSettingsPageComponent } from './settings/pages/vendor-settings-page/vendor-settings-page.component';
import { OnlinePaymentSetupPageComponent } from './settings/pages/online-payment-setup-page.component';
import { ContactPageComponent } from './public/pages/contact-page/contact-page.component';
import { VendorsListPageComponent } from './public/pages/vendors-list-page/vendors-list-page.component';
import { VendorPageComponent } from './public/pages/vendor-page/vendor-page.component';
import { VendorPrivacyPolicyPageComponent } from './public/pages/vendor-privacy-policy-page/vendor-privacy-policy-page.component';
import { RoomPageComponent } from './public/pages/room-page/room-page.component';
import { BookingPageComponent } from './public/pages/booking-page/booking-page.component';
import { BookingConfirmationPageComponent } from './public/pages/booking-confirmation-page/booking-confirmation-page.component';

const routes: Routes = [
  {
    path: '',
    component: HomePageComponent,
    title: 'Outil de réservation de salles pour les entreprises et les communes',
  },
  {
    path: 'a-propos',
    redirectTo: 'louer-ma-salle/a-propos',
    pathMatch: 'full',
  },
  {
    path: 'louer-ma-salle/a-propos',
    component: AboutPageComponent,
    title: 'À propos',
  },
  {
    path: 'tarifs',
    redirectTo: 'louer-ma-salle/tarifs',
    pathMatch: 'full',
  },
  {
    path: 'louer-ma-salle/tarifs',
    component: PricingPageComponent,
    title: 'Tarifs',
  },
  {
    path: 'contact',
    redirectTo: 'louer-ma-salle/contact',
    pathMatch: 'full',
  },
  {
    path: 'louer-ma-salle/contact',
    component: ContactPageComponent,
    title: 'Contact',
  },
  {
    path: 'louer-ma-salle/partenaires-revendeurs',
    component: PartnersResellersPageComponent,
  },
  {
    path: 'hote/:vendorSlug',
    redirectTo: 'louer-salle/:vendorSlug',
    pathMatch: 'full',
  },
  {
    path: 'louer-salle/:vendorSlug',
    component: VendorPageComponent,
    data: {
      b2c: true,
    },
  },
  {
    path: 'hote/:vendorSlug/politique-confidentialite',
    redirectTo: 'louer-salle/:vendorSlug/politique-confidentialite',
    pathMatch: 'full',
  },
  {
    path: 'louer-salle/:vendorSlug/politique-confidentialite',
    component: VendorPrivacyPolicyPageComponent,
    title: 'Politique de confidentialité',
    data: {
      b2c: true,
    },
  },
  {
    path: 'hote/:vendorSlug/salle/:id/reserver',
    redirectTo: 'louer-salle/:vendorSlug/salle/:id/reserver',
    pathMatch: 'full',
  },
  {
    path: 'louer-salle/:vendorSlug/salle/:id/reserver',
    component: BookingPageComponent,
    data: {
      b2c: true,
      hideFooter: true,
      grayBackground: true,
    },
  },
  {
    path: 'hote/:vendorSlug/salle/:id',
    redirectTo: 'louer-salle/:vendorSlug/salle/:id',
    pathMatch: 'full',
  },
  {
    path: 'louer-salle/:vendorSlug/salle/:id',
    component: RoomPageComponent,
    data: {
      b2c: true,
    },
  },
  {
    path: 'hote/:vendorSlug/salle/:id/confirmation',
    redirectTo: 'louer-salle/:vendorSlug/salle/:id/confirmation',
    pathMatch: 'full',
  },
  {
    path: 'louer-salle/:vendorSlug/salle/:id/confirmation',
    component: BookingConfirmationPageComponent,
    title: 'Confirmation de réservation',
    data: {
      b2c: true,
    },
  },
  {
    path: 'reservation/:token',
    component: MyBookingPage,
    title: 'Ma réservation',
    data: {
      b2c: true,
    },
  },
  {
    path: 'annuler-reservation/:token',
    redirectTo: 'reservation/:token',
    pathMatch: 'full',
  },
  {
    path: 'louer-salle',
    component: VendorsListPageComponent,
    title: 'Rechercher une salle',
  },
  {
    path: 'permissions',
    component: PermissionsPageComponent,
  },
  {
    path: 'conditions-generales',
    component: GeneralTermsPageComponent,
    title: 'Conditions générales',
  },
  {
    path: 'politique-confidentialite',
    component: PrivacyPolicyPageComponent,
    title: 'Politique de confidentialité',
  },
  {
    path: 'ouvrir-porte/:token',
    component: UnlockDoorPageComponent,
    data: {
      b2c: true,
      hideNavbarAndFooter: true,
    },
  },
  {
    path: 'auth',
    loadChildren: () =>
      import('./auth/auth.module').then((mod) => mod.AuthModule),
  },
  {
    path: 'utilisateurs',
    loadChildren: () =>
      import('./users/users.module').then((mod) => mod.UsersModule),
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    data: {
      manager: true,
    },
  },
  {
    path: 'salles',
    loadChildren: () =>
      import('./rooms/rooms.module').then((mod) => mod.RoomsModule),
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    data: {
      manager: true,
      requiredPermission: RoomPermission.Store,
    },
  },
  {
    path: 'reservations',
    loadChildren: () =>
      import('./bookings/bookings.module').then((mod) => mod.BookingsModule),
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    data: {
      manager: true,
    },
  },
  {
    path: 'facturation',
    loadChildren: () =>
      import('./invoices/invoices.module').then((mod) => mod.InvoicesModule),
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    data: {
      manager: true,
      requiredPermission: InvoicePermission.Store,
    },
  },
  {
    path: 'reglages',
    loadChildren: () =>
      import('./settings/settings.module').then((mod) => mod.SettingsModule),
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    data: {
      manager: true,
    },
  },
  {
    path: 'administration',
    redirectTo: 'administration/donnees',
  },
  {
    path: 'administration/donnees',
    component: VendorSettingsPageComponent,
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    canDeactivate: [RoomStepDirtyGuard],
    title: "Données de l'organisation",
    data: {
      manager: true,
      requiredPermission: VendorPermission.Update,
    },
  },
  {
    path: 'administration/paiement-en-ligne',
    component: OnlinePaymentSetupPageComponent,
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    canDeactivate: [RoomStepDirtyGuard],
    title: 'Paiement en ligne',
    data: {
      manager: true,
      requiredPermission: VendorPermission.Update,
    },
  },
  {
    path: 'compte',
    loadChildren: () =>
      import('./account/account.module').then((mod) => mod.AccountModule),
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    data: {
      manager: true,
    },
  },
  {
    path: 'codes-promo',
    loadChildren: () =>
      import('./coupons/coupons.module').then((mod) => mod.CouponsModule),
    canActivate: [AuthenticatedGuard],
    canActivateChild: [AuthenticatedGuard],
    data: {
      manager: true,
      requiredPermission: CouponPermission.Store,
    },
  },
  {
    path: '404',
    component: NotFoundPageComponent,
    title: 'Page introuvable',
  },
  {
    path: '**',
    redirectTo: '404',
  },
];

@NgModule({
  exports: [RouterModule],
  providers: [
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
  ],
})
export class AppRoutingModule {}
